'use client';

import React, { useState } from 'react';

import CatalogProductCard from '@/widgets/CatalogProductCard';
import { CatalogProductCardLoading } from '@/widgets/CatalogProductCard/CatalogProductCardLoading';
import { catalogItemFromOffer, catalogItemFromProduct, catalogSpecsFromGuarantee } from '@/widgets/CatalogProductCard/mappers';
import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';

import { useIsMobile } from '@/hooks/common/useMobile';

import { useCityStore } from '@/stores/city/useCityStore';

import { batteryBadges, isSuggestedBattery } from '@/constants/batteryProduct';
import { PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID } from '@/constants/common';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';

import { PageableProductsProps } from './models';

import { getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { ProductType } from '@/server/services/core/product/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { OfferContent } from '@/server/services/vendor/models';

export const PageableProducts = ({
    lastPage,
    cityId,
    pageSize,
    filterOptions,
    sourceType,
    attributeFilter,
    brandIds,
    sortBy = DEFAULT_PRODUCT_OFFER_SORT_BY,
    wideGrid = true
}: PageableProductsProps) => {
    const isMobile = useIsMobile();
    const selectedCityId = useCityStore((state) => state.cityId);
    const customerCityId = cityId ?? selectedCityId;
    const categoryId = filterOptions.categoryIds?.[0];
    const requestBrandIds = brandIds?.length ? brandIds : filterOptions.brandIds;
    const productsGridClassName = wideGrid
        ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
        : 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

    const [isLastPage, setIsLastPage] = useState(lastPage);

    const handleGetProducts = async (page: number) => {
        try {
            const response = await getProductsBatteryByVehicleFiltersService({
                ...filterOptions,
                page,
                cityId,
                size: pageSize,
                sortOption: PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID
            });
            setIsLastPage(response.data.last);
            return response?.data.content || [];
        } catch {
            return [];
        }
    };

    const handleGetFilteredProducts = async (page: number) => {
        try {
            const response = await getFilteredProductsService({
                ...(requestBrandIds?.[0] && { brandIds: requestBrandIds }),
                ...(filterOptions?.categoryIds?.[0] && { categoryIds: filterOptions.categoryIds }),
                cityId: customerCityId,
                page,
                size: pageSize,
                sortBy,
                ...(attributeFilter?.length ? { attributeFilter } : {})
            });
            setIsLastPage(response.data.last);
            return response?.data.content || [];
        } catch {
            return [];
        }
    };

    const handleLoadProducts = async (page: number): Promise<ProductType[] | OfferContent[]> => {
        try {
            if (sourceType === 'battery') {
                return await handleGetProducts(page);
            }
            return await handleGetFilteredProducts(page);
        } catch {
            return [];
        }
    };

    return (
        <div
            className='md:col-span-2'
            key={`${isMobile ? 'mobile' : 'desktop'}-${cityId}-${sourceType === 'battery' ? '' : `${customerCityId}`}-${attributeFilter?.join('|') ?? ''}-${brandIds?.join(',') ?? ''}-${sortBy}`}>
            <InfiniteScrollList<OfferContent | ProductBatteryType>
                fetchData={handleLoadProducts}
                renderRow={(product: OfferContent | ProductBatteryType) => {
                    if (product.discountPercentage !== 99) {
                        if (sourceType === 'battery') {
                            const batteryProduct = product as ProductBatteryType;
                            return (
                                <CatalogProductCard
                                    key={batteryProduct.uuid}
                                    item={catalogItemFromProduct(batteryProduct, { categoryId })}
                                    badge={isSuggestedBattery(batteryProduct) ? 'پیشنهاد ازکی‌سرویس' : undefined}
                                    specs={[
                                        ...batteryBadges(batteryProduct).map((badge) => badge.title),
                                        ...(catalogSpecsFromGuarantee(batteryProduct.guarantee) ?? [])
                                    ]}
                                />
                            );
                        }

                        const offerProduct = product as OfferContent;
                        return (
                            <CatalogProductCard key={offerProduct.productUuid} item={catalogItemFromOffer(offerProduct)} showSelectButton={false} />
                        );
                    }
                }}
                containerProps={{
                    className: `${productsGridClassName} p-2 pb-4 desktop-width`
                }}
                endMessage={null}
                infiniteScrollProps={{
                    loader: (
                        <div className={productsGridClassName}>
                            <CatalogProductCardLoading />
                        </div>
                    ),
                    scrollThreshold: 0.4
                }}
                defaultItems={[]}
                isLastPage={isLastPage}
            />
        </div>
    );
};
